import React, { useEffect, useMemo } from 'react';

import { FormDialog } from '@components/admin/FormDialog';
import { SimpleLabel, SimpleTextInput } from '@components/admin/Forms';
import { MultiDropdownInput } from '@components/admin/MultiDropdownInput';
import { Button } from '@components/Button';
import { LogForm } from '@components/Form';
import { FormAlert } from '@components/Forms/FormAlert';
import { RequiredTag } from '@components/Forms/RequiredTag';
import { SubmittingOverlay } from '@components/Forms/SubmittingOverlay';
import { agencyStrings } from '@data/agency';
import { getCityLabelByName, podCityNames } from '@data/cities';
import {
  routePlanScheduleFields,
  routePlanServiceNameStrings,
  routePlanStrings,
  weekdays,
} from '@data/routePlan';
import { vesselScheduleStrings } from '@data/vesselSchedule';
import { usePatchRoutePlan } from '@hooks/mutation/dashboard/usePatchRoutePlan';
import { usePostRoutePlan } from '@hooks/mutation/dashboard/usePostRoutePlan';
import { useWarehouses } from '@hooks/queries/dashboard/useWarehouses';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/RemoveCircleOutline';
import { Agency } from '@models/Agency';
import {
  routePlanForm,
  type RoutePlanForm,
  type ServiceName,
  routePlanFormInitialValues,
  serviceName,
  RoutePlan,
  mapRoutePlanToRoutePlanForm,
  RoutePlanSchedule,
  routePlanScheduleFormInitialValues,
  RoutePlanScheduleForm,
} from '@models/RoutePlan';
import { makeMuiStyles, useCommonStyles } from '@styles/index';
import { createValidate, joinKeyPath } from '@utils/forms';
import { createStrings } from '@utils/local';
import { iota } from '@utils/types';
import { FieldArray, Form, Formik, useField } from 'formik';
import { sortBy, uniq } from 'lodash-es';
import { unwrap } from 'tyrann-io/src';

const strings = createStrings({
  ja: {
    addItemsTitle: `${routePlanStrings.route}を追加する`,
    editItemsTitle: `${routePlanStrings.route}を編集する`,
    addSchedule: 'スケジュールを追加',
  },
});

const renderLabel = (label: string, required: boolean = true) => {
  return (
    <SimpleLabel width="auto">
      {label}
      {required && (
        <>
          <span style={{ width: '0.75rem', display: 'inline-block' }} />
          <RequiredTag />
        </>
      )}
    </SimpleLabel>
  );
};

const useStyles = makeMuiStyles((_theme, common) => ({
  body: {
    overflow: 'auto',
    maxWidth: '100%',
    width: '60rem',
  },
  routePlanAddItemContainer: {
    display: 'grid',
    gridTemplateColumns: 'auto repeat(12, 1fr)',
    columnGap: 10,
    rowGap: 20,
  },
  schedulesContainer: {
    gridColumn: 'span 12',
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr) auto',
    columnGap: 10,
    rowGap: 20,
  },
  scheduleAddButtonContainer: {
    gridColumn: 'span 13',
    ...common.flexInline,
    ...common.justifyEnd,
  },
}));

const WarehouseInputGroup: React.FC<{ agencyList: Agency[] }> = ({ agencyList }) => {
  const [agencyField] = useField<number>('agency_id');
  const [polField] = useField<string>('pol');
  const [receivingCityField, , receivingCityFieldHelpers] = useField<string>('receiving_city');
  const [receivingWarehousesField, , receivingWarehousesHelpers] =
    useField<number[]>('receiving_warehouses');

  const [exportWarehouseIdField, , exportWarehouseIdHelpers] =
    useField<number>('export_warehouse_id');

  const warehouseList = useWarehouses(
    { agency_id: agencyField.value },
    { enabled: !!agencyField.value },
  );

  const defaultOption = useMemo(
    () => ({
      label: '-',
      value: 0,
    }),
    [],
  );

  const exportWarehouseOptions = useMemo(
    () => [
      ...(warehouseList.data
        ?.filter((w) => w.city === polField.value)
        .map((d) => ({ label: d.name, value: d.id })) ?? []),
      defaultOption,
    ],
    [warehouseList.data, polField.value],
  );

  const receivingWarehouseOptions = useMemo(
    () => [
      ...(warehouseList.data
        ?.filter((w) => w.city === receivingCityField.value)
        .map((d) => ({ label: d.name, value: d.id })) ?? []),
    ],
    [warehouseList.data, receivingCityField.value],
  );

  useEffect(() => {
    const currentReceivingWarehouse = warehouseList.data?.find(
      (w) => w.id === receivingWarehousesField.value[0],
    );
    if (receivingCityField.value !== currentReceivingWarehouse?.city && warehouseList.isSuccess) {
      receivingWarehousesHelpers.setValue([]);
    }
  }, [receivingCityField.value]);

  useEffect(() => {
    const currentExportWarehouse = warehouseList.data?.find(
      (w) => w.id === exportWarehouseIdField.value,
    );
    if (polField.value !== currentExportWarehouse?.city && warehouseList.isSuccess) {
      exportWarehouseIdHelpers.setValue(0);
    }
  }, [polField.value]);

  const chinaCityNames = useMemo(
    () => uniq(sortBy(warehouseList.data?.map((c) => c.city))),
    [warehouseList.data],
  );

  useEffect(() => {
    if (chinaCityNames.length && !chinaCityNames.includes(receivingCityField.value)) {
      receivingCityFieldHelpers.setValue(chinaCityNames[0]);
    }
  }, [chinaCityNames]);

  return (
    <>
      {renderLabel(agencyStrings.agency)}

      <SimpleTextInput
        name="agency_id"
        textInputFullWidth
        containerStyle={{
          gridColumn: 'span 12',
        }}
        choose
        options={agencyList.map((item) => ({
          label: item.name_en_short,
          value: item.id,
        }))}
      />

      {renderLabel(routePlanStrings.route2)}

      <SimpleTextInput
        name="receiving_city"
        placeholder={routePlanStrings.receivingCity}
        textInputFullWidth
        containerStyle={{
          gridColumn: 'span 4',
        }}
        choose
        options={chinaCityNames.map((name) => ({
          label: getCityLabelByName(name),
          value: name,
        }))}
        disabled={chinaCityNames.length === 0}
      />

      <SimpleTextInput
        name="pol"
        placeholder={vesselScheduleStrings.pol}
        textInputFullWidth
        containerStyle={{
          gridColumn: 'span 4',
        }}
        choose
        options={chinaCityNames.map((name) => ({
          label: getCityLabelByName(name),
          value: name,
        }))}
      />

      <SimpleTextInput
        name="pod"
        placeholder={vesselScheduleStrings.pod}
        textInputFullWidth
        containerStyle={{
          gridColumn: 'span 4',
        }}
        choose
        options={podCityNames.map((name) => ({
          label: getCityLabelByName(name),
          value: name,
        }))}
      />

      {renderLabel(routePlanStrings.receivingWarehouse)}
      <MultiDropdownInput
        name="receiving_warehouses"
        containerStyle={{
          gridColumn: 'span 12',
        }}
        options={receivingWarehouseOptions}
      />

      {renderLabel(routePlanStrings.exportWarehouse)}
      <SimpleTextInput
        name="export_warehouse_id"
        textInputFullWidth
        containerStyle={{
          gridColumn: 'span 12',
        }}
        choose
        options={exportWarehouseOptions}
        disabled={exportWarehouseOptions.length === 1}
      />
    </>
  );
};

const ScheduleInput: React.FC<{ name: string; index: number; onDelete: () => void }> = ({
  name: parentName,
  index,
  onDelete,
}) => {
  const classes = { ...useStyles(), ...useCommonStyles() };
  const [scheduleField, ,] = useField<RoutePlanSchedule[]>('schedules');

  return (
    <div className={classes.schedulesContainer}>
      {routePlanScheduleFields.map(({ name, label }) => (
        <SimpleTextInput
          textInputWidth={40}
          placeholder={label}
          name={joinKeyPath(parentName, index, name)}
          textInputFullWidth
          containerStyle={{
            gridColumn: 'span 1',
          }}
          choose
          options={iota(7).map((i) => ({ label: weekdays[i], value: i }))}
        />
      ))}
      <RemoveIcon
        color={scheduleField.value?.length > 1 ? 'action' : 'disabled'}
        className={classes.baseButton}
        onClick={scheduleField.value?.length > 1 ? onDelete : undefined}
      />
    </div>
  );
};

const ScheduleArrayInput: React.FC<{}> = () => {
  const classes = { ...useStyles(), ...useCommonStyles() };
  const [scheduleField, ,] = useField<RoutePlanSchedule[]>('schedules');

  return (
    <FieldArray
      name="schedules"
      render={({
        remove,
        insert,
      }: {
        remove: (index: number) => void;
        insert: (index: number, value: RoutePlanScheduleForm) => void;
      }) => (
        <>
          {scheduleField.value?.map((_, index) => (
            <>
              {renderLabel(`${routePlanStrings.schedule} ${index + 1}`)}
              <ScheduleInput
                key={index}
                name="schedules"
                index={index}
                onDelete={() => remove(index)}
              />
            </>
          ))}
          <div className={classes.scheduleAddButtonContainer}>
            <Button
              shape="round"
              type="button"
              size="small"
              color="primary"
              text={
                <>
                  <AddIcon style={{ height: 15 }} />
                  {strings.addSchedule}
                </>
              }
              onClick={() =>
                insert((scheduleField.value.length ?? 0) + 1, routePlanScheduleFormInitialValues)
              }
            />
          </div>
        </>
      )}
    />
  );
};

export interface RoutePlanAddItemDialogProps {
  open: boolean;
  routePlan?: RoutePlan;
  agencyList: Agency[];
  onClose: () => void;
}

export const RoutePlanAddItemDialog: React.FC<RoutePlanAddItemDialogProps> = (props) => {
  const { open, agencyList, routePlan, onClose } = props;

  const isEdit = !!routePlan;

  const initialValues = useMemo(() => {
    return routePlan ? mapRoutePlanToRoutePlanForm(routePlan) : routePlanFormInitialValues;
  }, [routePlan]);

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const postRoutePlan = usePostRoutePlan();

  const patchRoutePlan = usePatchRoutePlan(routePlan?.id!);

  return (
    <FormDialog
      dialogProps={{ maxWidth: false }}
      title={isEdit ? strings.editItemsTitle : strings.addItemsTitle}
      alignTitle="center"
      open={open}
      onClose={onClose}
      classes={{ body: classes.body }}
    >
      <Formik<RoutePlanForm>
        initialValues={initialValues}
        validate={createValidate(routePlanForm)}
        onSubmit={async (_values) => {
          const values = unwrap(routePlanForm, _values);

          const baseData = {
            ...values,
            agency_id: values.agency_id!,
            export_warehouse_id: values.export_warehouse_id!,
            receiving_warehouses: values.receiving_warehouses as number[],
            name: `${getCityLabelByName(values.receiving_city)}${
              values.receiving_city === values.pol ? '' : getCityLabelByName(values.pol)
            }${getCityLabelByName(values.pod)}航路`,
            service_name: values.service_name as ServiceName,
          };

          if (isEdit) {
            await patchRoutePlan.mutateAsync({
              ...baseData,
            });
          } else {
            await postRoutePlan.mutateAsync({
              ...baseData,
              disabled: true,
            });
          }
        }}
      >
        <Form className={classes.routePlanAddItemContainer}>
          <LogForm name="RoutePlanAddItemDialog" />

          <div style={{ gridColumn: 'span 13' }}>
            <SubmittingOverlay />
            <FormAlert mutation={postRoutePlan} />
          </div>

          {renderLabel(routePlanStrings.serviceName)}

          <SimpleTextInput
            name="service_name"
            textInputFullWidth
            containerStyle={{
              gridColumn: 'span 12',
            }}
            choose
            options={serviceName.types.map((t) => ({
              label: routePlanServiceNameStrings[t.value],
              value: t.value,
            }))}
          />

          <WarehouseInputGroup agencyList={agencyList} />

          <ScheduleArrayInput />
        </Form>
      </Formik>
    </FormDialog>
  );
};
